import type { SeoSidebar } from '../store/types';

export const INTRO_TEXT_DELAY_MS = 6000;
export const INTRO_CRAWL_SWAP_DELAY_MS = 500;
export const INTRO_ANIMATION_COMPLETE_MS = 10000;
export const ITEMS_FADE_IN_DELAY_MS = 500;
export const SIDEBAR_CLOSE_ANIMATION_MS = 1000;

export interface ResetIntroOptions {
  keepCanvasVisible?: boolean;
  showCrawlImmediately?: boolean;
  useVisibleZoom?: boolean;
}

export const normalizePathname = (pathname: string): string => {
  const trimmed = pathname.replace(/\/+$/, '');
  if (!trimmed) return '/';
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
};

export const isSubRoutePath = (pathname: string): boolean => normalizePathname(pathname) !== '/';

export const getSidebarFromPathname = (pathname: string): SeoSidebar => {
  if (pathname === '/about') return 'about';
  if (pathname === '/services') return 'services';
  if (pathname === '/connect') return 'connect';
  if (pathname === '/my-portfolio' || pathname.startsWith('/my-portfolio/')) return 'portfolio';
  return '';
};

export const getPortfolioSlugFromPathname = (pathname: string): string | null => {
  if (!pathname.startsWith('/my-portfolio/')) return null;
  const slug = pathname.replace('/my-portfolio/', '').trim();
  if (!slug) return null;
  try {
    return decodeURIComponent(slug);
  } catch {
    return slug;
  }
};

export const getPathForSidebar = (sidebar: SeoSidebar): string => {
  switch (sidebar) {
    case 'about':
      return '/about';
    case 'services':
      return '/services';
    case 'connect':
      return '/connect';
    case 'portfolio':
      return '/my-portfolio';
    default:
      return '/';
  }
};

export const getVisualPathname = ({
  didEnterViaSubRoute,
  deepLinkSidebarReady,
  deferStartupRoute,
  pathname,
}: {
  didEnterViaSubRoute: boolean;
  deepLinkSidebarReady: boolean;
  deferStartupRoute: boolean;
  pathname: string;
}): string => {
  if (didEnterViaSubRoute && !deepLinkSidebarReady) return '/';
  if (deferStartupRoute && !didEnterViaSubRoute) return '/';
  return pathname;
};

export const shouldSkipInitialDeepLinkModelAnimation = ({
  didEnterViaSubRoute,
  deepLinkSidebarReady,
  shouldResetAfterFirstDeepClose,
  activeSidebar,
  initialDeepLinkSidebar,
}: {
  didEnterViaSubRoute: boolean;
  deepLinkSidebarReady: boolean;
  shouldResetAfterFirstDeepClose: boolean;
  activeSidebar: SeoSidebar;
  initialDeepLinkSidebar: SeoSidebar;
}): boolean =>
  didEnterViaSubRoute &&
  deepLinkSidebarReady &&
  shouldResetAfterFirstDeepClose &&
  activeSidebar !== '' &&
  activeSidebar === initialDeepLinkSidebar;
